/**
 * Just Carry On — storefront behaviour.
 *
 * Everything here is an enhancement: content, navigation and forms work without it.
 * Header (transparent over the hero, mega menu, phone drawer), search sheet with
 * predictive results, section reveals, product-card tilt and quick view, detail tabs,
 * the size-guide dialog, the phone dock and generic quantity steppers.
 */
(function () {
  'use strict';

  window.__jcoReady = true;

  var JCO = (window.JustCarryOn = window.JustCarryOn || {});
  var root = document.documentElement;
  var body = document.body;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var desktop = window.matchMedia('(min-width: 990px)');

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /* ------------------------------------------------------------------ *
   * Focus handling shared by drawers and sheets
   * ------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var items = $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  var openLayers = [];

  function lockPage(on) {
    body.classList.toggle('drawer-open', on);
    body.classList.toggle('mbar-suppressed', on);
    root.style.overflow = on ? 'hidden' : '';
  }

  /** Opens a drawer or sheet: shows it, locks the page, traps focus, closes on Escape. */
  JCO.openLayer = function (layer, opts) {
    opts = opts || {};
    if (!layer || layer.classList.contains('is-open')) return;
    var returnFocus = document.activeElement;
    var onKey = function (event) {
      if (event.key === 'Escape') { JCO.closeLayer(layer); return; }
      trapFocus(layer, event);
    };
    layer.__jco = { returnFocus: returnFocus, onKey: onKey, scrim: opts.scrim, trigger: opts.trigger, onClose: opts.onClose };
    if (layer.hasAttribute('hidden')) layer.removeAttribute('hidden');
    // Next frame, so the transition runs from the closed state.
    requestAnimationFrame(function () {
      layer.classList.add('is-open');
      if (opts.scrim) opts.scrim.classList.add('is-open');
    });
    if (opts.trigger) opts.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', onKey);
    openLayers.push(layer);
    lockPage(true);
    var focusTarget = opts.focus || layer;
    setTimeout(function () { focusTarget.focus({ preventScroll: true }); }, 60);
  };

  JCO.closeLayer = function (layer) {
    if (!layer || !layer.classList.contains('is-open')) return;
    var state = layer.__jco || {};
    layer.classList.remove('is-open');
    if (state.scrim) state.scrim.classList.remove('is-open');
    if (state.trigger) state.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', state.onKey);
    openLayers = openLayers.filter(function (l) { return l !== layer; });
    if (!openLayers.length) lockPage(false);
    if (state.onClose) state.onClose();
    if (state.returnFocus && state.returnFocus.focus) state.returnFocus.focus({ preventScroll: true });
    if (location.hash === '#' + layer.id) history.replaceState(null, '', location.pathname + location.search);
  };

  /* ------------------------------------------------------------------ *
   * Toast
   * ------------------------------------------------------------------ */
  var toastTimer;
  JCO.toast = function (message) {
    var toast = $('[data-toast]');
    if (!toast) return;
    $('[data-toast-message]', toast).textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toast.classList.remove('is-visible'); }, 2600);
  };

  /* ------------------------------------------------------------------ *
   * Section reveals — fade up 28px as sections enter, staggered by --reveal-delay
   * ------------------------------------------------------------------ */
  function initReveals() {
    var items = $$('[data-reveal]');
    if (!root.classList.contains('anim') || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------ *
   * Header: transparent over the hero, measured height
   * ------------------------------------------------------------------ */
  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    var setHeight = function () {
      var bar = $('.site-header__bar', header);
      if (bar) root.style.setProperty('--site-header-height', bar.offsetHeight + 'px');
    };
    setHeight();
    window.addEventListener('resize', setHeight);

    if (!header.classList.contains('site-header--clear-capable')) return;
    var firstSection = $('main > .shopify-section');
    var hero = firstSection && $('[data-hero]', firstSection);
    if (!hero) return;

    var ticking = false;
    var update = function () {
      ticking = false;
      var heroBottom = hero.getBoundingClientRect().bottom;
      var headerHeight = header.offsetHeight;
      var menuOpen = header.querySelector('.mega.is-open');
      header.classList.toggle('is-clear', heroBottom > headerHeight + 8 && !menuOpen);
    };
    header.__updateClear = update;
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------------------ *
   * Mega menu — hover intent on a fine pointer, click and keyboard everywhere
   * ------------------------------------------------------------------ */
  function initMegaMenu() {
    var header = $('[data-header]');
    $$('[data-mega-item]').forEach(function (item) {
      var trigger = $('[data-mega-trigger]', item);
      var panel = $('[data-mega]', item);
      if (!trigger || !panel) return;
      var closeTimer;

      var open = function () {
        clearTimeout(closeTimer);
        $$('[data-mega].is-open').forEach(function (other) {
          if (other !== panel) {
            other.classList.remove('is-open');
            var t = $('[data-mega-trigger]', other.parentElement);
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
        panel.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        if (header && header.__updateClear) header.__updateClear();
      };
      var close = function () {
        clearTimeout(closeTimer);
        panel.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        if (header && header.__updateClear) header.__updateClear();
      };
      var closeSoon = function () {
        clearTimeout(closeTimer);
        closeTimer = setTimeout(close, 140);
      };

      trigger.addEventListener('click', function () {
        if (panel.classList.contains('is-open')) close(); else open();
      });
      item.addEventListener('mouseenter', function () { if (finePointer.matches) open(); });
      item.addEventListener('mouseleave', function () { if (finePointer.matches) closeSoon(); });
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) close();
      });
      item.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && panel.classList.contains('is-open')) {
          close();
          trigger.focus();
        }
      });
      item.__closeMega = close;
    });

    // Escape closes an open panel wherever focus happens to be.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      $$('[data-mega-item]').forEach(function (item) {
        if (item.__closeMega && $('[data-mega].is-open', item)) item.__closeMega();
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Phone menu drawer + accordion rows
   * ------------------------------------------------------------------ */
  function initMenuDrawer() {
    var drawer = $('[data-menu-drawer]');
    if (!drawer) return;
    var scrim = $('[data-menu-scrim]');
    var trigger = $('[data-menu-open]');

    if (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        JCO.openLayer(drawer, { scrim: scrim, trigger: trigger });
      });
    }
    $$('[data-menu-close]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        JCO.closeLayer(drawer);
      });
    });
    if (scrim) scrim.addEventListener('click', function () { JCO.closeLayer(drawer); });
    // Opened by the no-JS fallback (#menu-drawer) before this script arrived.
    if (location.hash === '#menu-drawer') {
      history.replaceState(null, '', location.pathname + location.search);
    }

    $$('[data-accordion]', drawer).forEach(function (button) {
      var panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      panel.hidden = true;
      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        if (reducedMotion.matches) { panel.hidden = expanded; return; }
        if (expanded) {
          panel.style.height = panel.scrollHeight + 'px';
          panel.classList.add('is-animating');
          requestAnimationFrame(function () { panel.style.height = '0px'; });
          setTimeout(function () { panel.hidden = true; panel.classList.remove('is-animating'); panel.style.height = ''; }, 330);
        } else {
          panel.hidden = false;
          panel.style.height = '0px';
          panel.classList.add('is-animating');
          requestAnimationFrame(function () { panel.style.height = panel.scrollHeight + 'px'; });
          setTimeout(function () { panel.classList.remove('is-animating'); panel.style.height = ''; }, 330);
        }
      });
    });

    desktop.addEventListener('change', function (event) {
      if (event.matches) JCO.closeLayer(drawer);
    });
  }

  /* ------------------------------------------------------------------ *
   * Search sheet: popular searches, then suggestions as you type — search terms,
   * collections, products and pages, a combobox the arrow keys move through
   * ------------------------------------------------------------------ */
  function initSearch() {
    var sheet = $('[data-search-sheet]');
    if (!sheet) return;
    var input = $('[data-search-input]', sheet);
    var suggestions = $('[data-search-suggestions]', sheet);
    var popular = $('[data-search-popular]', sheet);
    var clear = $('[data-search-clear]', sheet);
    var status = $('[data-search-status]', sheet);
    var routes = JCO.routes || {};
    var controller;
    var timer;
    var active = -1;

    var open = function (event, trigger) {
      if (event) event.preventDefault();
      JCO.openLayer(sheet, {
        trigger: trigger,
        focus: input,
        // The sheet fades out, then leaves the page entirely so it can't catch clicks.
        onClose: function () {
          setTimeout(function () { if (!sheet.classList.contains('is-open')) sheet.hidden = true; }, 300);
        },
      });
    };
    $$('[data-search-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) { open(event, trigger); });
    });
    $$('[data-search-close]', sheet).forEach(function (btn) {
      btn.addEventListener('click', function () { JCO.closeLayer(sheet); });
    });
    sheet.addEventListener('click', function (event) {
      if (event.target === sheet) JCO.closeLayer(sheet);
    });

    var items = function () { return $$('[data-suggest-item]', suggestions); };
    var highlight = function (index) {
      var list = items();
      list.forEach(function (el, i) {
        el.classList.toggle('is-active', i === index);
        el.setAttribute('aria-selected', String(i === index));
      });
      if (list[index]) {
        list[index].scrollIntoView({ block: 'nearest' });
        input.setAttribute('aria-activedescendant', list[index].id);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
      active = index;
    };

    /** Puts suggestions in place; the popular searches show whenever there are none. */
    var show = function (html) {
      suggestions.innerHTML = html || '';
      var listbox = $('[role="listbox"]', suggestions);
      input.setAttribute('aria-expanded', String(Boolean(listbox)));
      if (popular) popular.hidden = Boolean(listbox);
      var summary = $('[data-predictive-count], .mbar-search__note', suggestions);
      if (status) status.textContent = summary ? summary.textContent.trim() : '';
      highlight(-1);
    };

    var fetchSuggestions = function (terms) {
      if (controller) controller.abort();
      if (!terms) { show(''); return; }
      controller = new AbortController();
      // Search terms, collections, products and pages, up to six of each.
      var url = (routes.predictiveSearch || '/search/suggest')
        + '?q=' + encodeURIComponent(terms)
        + '&resources%5Btype%5D=query,collection,product,page'
        + '&resources%5Blimit%5D=6&resources%5Blimit_scope%5D=each'
        + '&resources%5Boptions%5D%5Bunavailable_products%5D=last'
        + '&section_id=predictive-search';
      fetch(url, { signal: controller.signal })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('.shopify-section') || doc.body;
          show(section.innerHTML.trim());
        })
        .catch(function (err) { if (err.name !== 'AbortError') show(''); });
    };

    input.addEventListener('input', function () {
      clearTimeout(timer);
      var terms = input.value.trim();
      if (clear) clear.hidden = !input.value;
      timer = setTimeout(function () { fetchSuggestions(terms); }, 180);
    });
    if (clear) {
      clear.addEventListener('click', function () {
        clearTimeout(timer);
        if (controller) controller.abort();
        input.value = '';
        clear.hidden = true;
        show('');
        input.focus();
      });
    }
    input.addEventListener('keydown', function (event) {
      var list = items();
      if (!list.length) return;
      if (event.key === 'ArrowDown') { event.preventDefault(); highlight(Math.min(active + 1, list.length - 1)); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); highlight(Math.max(active - 1, 0)); }
      else if (event.key === 'Enter' && active > -1 && list[active]) { event.preventDefault(); list[active].click(); }
    });
  }

  /* ------------------------------------------------------------------ *
   * Product-card tilt — a slight 3D lean toward the pointer
   * ------------------------------------------------------------------ */
  function initTilt() {
    if (!JCO.tilt || reducedMotion.matches || !finePointer.matches) return;
    document.addEventListener('pointermove', function (event) {
      var card = event.target.closest && event.target.closest('[data-tilt]');
      if (!card) return;
      var rect = card.getBoundingClientRect();
      var x = (event.clientX - rect.left) / rect.width - 0.5;
      var y = (event.clientY - rect.top) / rect.height - 0.5;
      card.style.setProperty('--tilt-x', (-y * 4).toFixed(2) + 'deg');
      card.style.setProperty('--tilt-y', (x * 4).toFixed(2) + 'deg');
    }, { passive: true });
    document.addEventListener('pointerout', function (event) {
      var card = event.target.closest && event.target.closest('[data-tilt]');
      if (card && !card.contains(event.relatedTarget)) {
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Quick view — a card's hover bar opens the product in a dialog. Shopify renders
   * sections/quick-view.liquid for that product (Section Rendering API) and product.js
   * wires its form; a failed load falls back to the trigger's own link, the product page.
   *
   * Quick add is the same dialog, compact: a card's Add to cart or Buy now on a product
   * with options, and the phone dock's Add to cart, open it to choose them.
   * ------------------------------------------------------------------ */
  function initQuickView() {
    var layer = $('[data-quick-view]');
    if (!layer) return;
    var content = $('[data-quick-view-body]', layer);
    var cache = {};
    var TRIGGER = '[data-quick-view-open], [data-quick-add]';

    var load = function (url) {
      if (!cache[url]) {
        cache[url] = fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=quick-view')
          .then(function (res) {
            if (!res.ok) throw new Error('Quick view ' + res.status);
            return res.text();
          })
          .catch(function (err) { delete cache[url]; throw err; });
      }
      return cache[url];
    };

    /** Opens the product at `url` (a product URL, optionally with ?variant=). */
    JCO.openQuickView = function (url, opts) {
      opts = opts || {};
      var trigger = opts.trigger;
      if (trigger && trigger.getAttribute('aria-busy') === 'true') return;
      if (trigger) trigger.setAttribute('aria-busy', 'true');
      load(url)
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var section = doc.querySelector('.shopify-section') || doc.body;
          if (!section.querySelector('[data-product-json]')) throw new Error('Quick view is empty');
          content.innerHTML = section.innerHTML;
          content.scrollTop = 0;
          layer.classList.toggle('quick-view--add', opts.mode === 'add');
          JCO.openLayer(layer, {
            trigger: trigger,
            // Fades out, then leaves the page so it can't catch clicks.
            onClose: function () {
              setTimeout(function () { if (!layer.classList.contains('is-open')) layer.hidden = true; }, 320);
            },
          });
          // Wired once shown, so the gallery can measure itself.
          if (JCO.initProduct) JCO.initProduct(content, { quickView: true });
        })
        .catch(function () { window.location.href = url; })
        .finally(function () { if (trigger) trigger.removeAttribute('aria-busy'); });
    };

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest(TRIGGER);
      // A modified click still opens the product page, in a new tab or window.
      if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      JCO.openQuickView(trigger.getAttribute('href'), {
        trigger: trigger,
        mode: trigger.hasAttribute('data-quick-add') ? 'add' : 'view',
      });
    });
    // Start loading as the pointer reaches a trigger, so the dialog opens straight away.
    document.addEventListener('pointerover', function (event) {
      var trigger = event.target.closest && event.target.closest(TRIGGER);
      if (trigger) load(trigger.getAttribute('href')).catch(function () {});
    });
    $$('[data-quick-view-close]', layer).forEach(function (btn) {
      btn.addEventListener('click', function () { JCO.closeLayer(layer); });
    });
    layer.addEventListener('click', function (event) {
      if (event.target === layer) JCO.closeLayer(layer);
    });
  }

  /* ------------------------------------------------------------------ *
   * Product-card swatches — a colour shows its own photo, and carries into the card's
   * links: the product page and the quick view open on that colour
   * ------------------------------------------------------------------ */
  function initCardSwatches() {
    document.addEventListener('click', function (event) {
      var swatch = event.target.closest && event.target.closest('[data-swatch]');
      var card = swatch && swatch.closest('[data-product-card]');
      if (!card) return;
      event.preventDefault();
      $$('[data-swatch]', card).forEach(function (s) { s.setAttribute('aria-pressed', String(s === swatch)); });
      var image = $('.product-card__image--primary', card);
      if (image && swatch.getAttribute('data-src')) {
        image.srcset = swatch.getAttribute('data-srcset') || '';
        image.src = swatch.getAttribute('data-src');
      }
      card.classList.add('is-swatched');
      var base = card.getAttribute('data-product-url');
      var url = base + (base.indexOf('?') > -1 ? '&' : '?') + 'variant=' + swatch.getAttribute('data-variant-id');
      $$('[data-product-link]', card).forEach(function (link) { link.setAttribute('href', url); });
    });
  }

  /* ------------------------------------------------------------------ *
   * Detail tabs — every panel is in the HTML; only the inactive ones are hidden
   * ------------------------------------------------------------------ */
  function initTabs() {
    $$('[data-tabs]').forEach(function (group) {
      var tabs = $$('[data-tab]', group);
      if (!tabs.length) return;
      var select = function (tab, focus) {
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        if (focus) tab.focus();
      };
      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () { select(tab); });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (next) { event.preventDefault(); select(next, true); }
        });
      });
      select(tabs[0]);
    });
  }

  /* ------------------------------------------------------------------ *
   * Size-guide dialog
   * ------------------------------------------------------------------ */
  function initSizeGuide() {
    var dialog = $('[data-size-guide]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    $$('[data-size-guide-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        dialog.showModal();
      });
    });
    $$('[data-size-guide-close]', dialog).forEach(function (btn) {
      btn.addEventListener('click', function () { dialog.close(); });
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });
  }

  /* ------------------------------------------------------------------ *
   * Phone dock — always in view. On a product page its Add to cart opens up once the
   * page's own button is scrolled past. A product with options opens the quick add on
   * the choice made on the page, to confirm or change it; one without adds straight away.
   * ------------------------------------------------------------------ */
  function initMobileBar() {
    var bar = $('[data-mbar]');
    if (!bar) return;
    var atc = $('[data-mbar-atc]', bar);
    var section = $('[data-product-section]');
    var pageButton = section && $('[data-add-button]', section);
    if (!atc || !pageButton || !('IntersectionObserver' in window)) return;
    // Watch both of the page's buttons, and count them gone once they pass under the
    // sticky header rather than off the top of the screen.
    var watched = pageButton.closest('.product-template__actions') || pageButton;
    var headerBar = $('[data-header] .site-header__bar');
    var headerHeight = headerBar ? headerBar.offsetHeight : 0;
    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var top = entry.rootBounds ? entry.rootBounds.top : headerHeight;
      var above = !entry.isIntersecting && entry.boundingClientRect.top < top;
      bar.setAttribute('data-atc', above ? 'on' : 'off');
      atc.tabIndex = above ? 0 : -1;
      if (above) atc.removeAttribute('aria-hidden'); else atc.setAttribute('aria-hidden', 'true');
    }, { rootMargin: '-' + headerHeight + 'px 0px 0px 0px' }).observe(watched);
    atc.addEventListener('click', function () {
      var picked = pageButton.form && $('[data-variant-select]', pageButton.form);
      if ($('[data-option]', section) && picked && JCO.openQuickView) {
        JCO.openQuickView(location.pathname + '?variant=' + picked.value, { mode: 'add', trigger: atc });
        return;
      }
      if (!pageButton.disabled) pageButton.click();
    });
  }

  /* ------------------------------------------------------------------ *
   * Quantity steppers (product form; cart lines are handled by cart.js)
   * ------------------------------------------------------------------ */
  function initSteppers() {
    document.addEventListener('click', function (event) {
      var step = event.target.closest && event.target.closest('[data-qty-step]');
      if (!step || step.closest('[data-cart-line]')) return;
      var wrap = step.closest('.product-template__qty');
      var input = wrap && $('[data-qty-input]', wrap);
      if (!input) return;
      var min = parseInt(input.min || '1', 10);
      var next = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(step.getAttribute('data-qty-step'), 10));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ------------------------------------------------------------------ *
   * Collection sort — apply on change
   * ------------------------------------------------------------------ */
  function initSort() {
    $$('[data-sort-select]').forEach(function (select) {
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  function init() {
    initReveals();
    initHeader();
    initMegaMenu();
    initMenuDrawer();
    initSearch();
    initTilt();
    initQuickView();
    initCardSwatches();
    initTabs();
    initSizeGuide();
    initMobileBar();
    initSteppers();
    initSort();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
